/**
 * useUserProfileDropdowns Hook
 * Manages all dropdown states for User Profile component using consolidated state
 */

import { useState, useCallback, useRef } from "react";

// Initial dropdown state
const initialDropdownState = {
  userType: false,
  dept: false,
  wxCompany: false,
  contract: false,
  overheadCostType: false,
  currency: false,
  country: false,
  state: false,
  profileImage: false,
};

// Initial search term state
const initialSearchTermState = {
  currency: "",
  country: "",
  state: "",
};

export const useUserProfileDropdowns = () => {
  // Consolidated dropdown open states
  const [dropdowns, setDropdowns] = useState(initialDropdownState);
  
  // Consolidated search term states
  const [searchTerms, setSearchTerms] = useState(initialSearchTermState);
  
  // Individual dropdown states (for backward compatibility)
  const [userTypeDropdownOpen, setUserTypeDropdownOpen] = useState(false);
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const [wxComapnyDropdownOpen, setWxComapnyDropdownOpen] = useState(false);
  const [contractDropdownOpen, setContractDropdownOpen] = useState(false);
  const [overheadCostTypeDropdownOpen, setOverheadCostTypeDropdownOpen] = useState(false);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  
  // Individual search term states (for backward compatibility)
  const [currencySearchTerm, setCurrencySearchTerm] = useState("");
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  
  // Refs
  const userTypeDropdownRef = useRef(null);
  const deptDropdownRef = useRef(null);
  const wxComapnyDropdownRef = useRef(null);
  const contractDropdownRef = useRef(null);
  const overheadCostTypeDropdownRef = useRef(null);
  const currencyDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Update dropdown state
  const updateDropdowns = useCallback((updates) => {
    setDropdowns((prev) => {
      const newState = { ...prev, ...updates };
      
      // Sync individual states
      setUserTypeDropdownOpen(newState.userType);
      setDeptDropdownOpen(newState.dept);
      setWxComapnyDropdownOpen(newState.wxCompany);
      setContractDropdownOpen(newState.contract);
      setOverheadCostTypeDropdownOpen(newState.overheadCostType);
      setCurrencyDropdownOpen(newState.currency);
      setCountryDropdownOpen(newState.country);
      setStateDropdownOpen(newState.state);
      setDropdownVisible(newState.profileImage);
      
      return newState;
    });
  }, []);
  
  // Update search term state
  const updateSearchTerms = useCallback((updates) => {
    setSearchTerms((prev) => {
      const newState = { ...prev, ...updates };
      
      // Sync individual states
      setCurrencySearchTerm(newState.currency);
      setCountrySearchTerm(newState.country);
      setStateSearchTerm(newState.state);
      
      return newState;
    });
  }, []);
  
  // Toggle dropdown
  const toggleDropdown = useCallback((dropdownName) => {
    updateDropdowns({
      [dropdownName]: !dropdowns[dropdownName],
    });
  }, [dropdowns, updateDropdowns]);
  
  // Close dropdown
  const closeDropdown = useCallback((dropdownName) => {
    updateDropdowns({
      [dropdownName]: false,
    });
  }, [updateDropdowns]);
  
  // Close all dropdowns
  const closeAllDropdowns = useCallback(() => {
    setDropdowns(initialDropdownState);
    setUserTypeDropdownOpen(false);
    setDeptDropdownOpen(false);
    setWxComapnyDropdownOpen(false);
    setContractDropdownOpen(false);
    setOverheadCostTypeDropdownOpen(false);
    setCurrencyDropdownOpen(false);
    setCountryDropdownOpen(false);
    setStateDropdownOpen(false);
    setDropdownVisible(false);
  }, []);
  
  // Set search term
  const setSearchTerm = useCallback((dropdownName, value) => {
    updateSearchTerms({
      [dropdownName]: value,
    });
  }, [updateSearchTerms]);
  
  return {
    // Consolidated states
    dropdowns,
    searchTerms,
    
    // Individual dropdown states (for backward compatibility)
    userTypeDropdownOpen,
    deptDropdownOpen,
    wxComapnyDropdownOpen,
    contractDropdownOpen,
    overheadCostTypeDropdownOpen,
    currencyDropdownOpen,
    countryDropdownOpen,
    stateDropdownOpen,
    dropdownVisible,
    
    // Individual search term states (for backward compatibility)
    currencySearchTerm,
    countrySearchTerm,
    stateSearchTerm,
    
    // Refs
    userTypeDropdownRef,
    deptDropdownRef,
    wxComapnyDropdownRef,
    contractDropdownRef,
    overheadCostTypeDropdownRef,
    currencyDropdownRef,
    countryDropdownRef,
    stateDropdownRef,
    dropdownRef,
    
    // Handlers
    toggleDropdown,
    closeDropdown,
    closeAllDropdowns,
    setSearchTerm,
    
    // Direct setters (for backward compatibility)
    setUserTypeDropdownOpen,
    setDeptDropdownOpen,
    setWxComapnyDropdownOpen,
    setContractDropdownOpen,
    setOverheadCostTypeDropdownOpen,
    setCurrencyDropdownOpen,
    setCountryDropdownOpen,
    setStateDropdownOpen,
    setDropdownVisible,
    setCurrencySearchTerm,
    setCountrySearchTerm,
    setStateSearchTerm,
    
    // Consolidated state setters
    setDropdowns,
    setSearchTerms,
  };
};
